/**
 * Orquestación de la marcación en Staff (004 · US1 · T029/T030; US2 · T036; US6 · T065; US8 · T061).
 *
 * Une la captura (`src/camera/`), el acumulador de evidencia (`frames.ts`), la ubicación en
 * paralelo (`src/geo/`), la máquina de estados (`machine.ts`) y las rutas del realm staff
 * (`api.ts`):
 *
 * 1. **Permiso primero** (FR-002/§8.1): la hoja explica para qué se usa la cámara y el permiso del
 *    sistema se dispara al tocarla, nunca al entrar.
 * 2. **Cámara abierta una sola vez** para todo el intento: buscar el Bootstrap Code, leer los
 *    frames y reintentar no cierran ni reabren la cámara (reabrirla perdería el enfoque).
 * 2b. **Ubicación en paralelo** (US6, T065): se pide en el mismo instante en que se abre la cámara
 *    (R-24). Al tener la evidencia lista, si la lectura sigue en curso se la espera como máximo
 *    `LOCATION_GRACE_MS` (1,5 s, QA-3 de la 005), recortado para que el envío nunca pase del
 *    presupuesto de cada modo (`location-grace.ts`); después se envía sin ella. Una respuesta que llega tarde se descarta (un
 *    número de generación por apertura de cámara evita que una lectura de un intento viejo se cuele
 *    en uno nuevo). El resultado se **reenvía en `verify`** (y en `POST /marks/autonomous`): el
 *    servidor ignora `body.location` de `POST /challenges` (wiring §10, DRIFT), así que mandarlo
 *    solo ahí lo perdería.
 * 3. **Bootstrap** → `POST /challenges`, con la acción mostrada **solo** para detectar discrepancia
 *    (FR-007). Del QR no se interpreta ni se guarda nada más (FR-004). Si el servidor responde
 *    `ACTION_CHANGED` (no llegó a crear challenge), la máquina pasa a `ACTION_CHANGED` con la
 *    acción corregida y espera **confirmación explícita** (`confirmActionChanged`, US2/T036) antes
 *    de volver a buscar el marcador — nunca se reproduce un challenge que no existió.
 * 3b. **Frame autónomo `FM1A`** (US8, T061): si lo primero que lee la cámara no es un Bootstrap
 *    Code sino un frame del modo sin conexión del marcador, no hay ningún `POST /challenges` que
 *    pedir — los frames leídos **son** la evidencia. Se acumulan con `collectAutonomousFrame` y se
 *    envían con `submitAutonomousMark` en cuanto hay suficientes o se cumple el plazo. Para el
 *    trabajador la pantalla es idéntica (mismo `phase` `CHALLENGE`, mismo "Verificando…").
 * 4. **Frames** → se acumulan con instantes **relativos** (`performance.now()`, R-6) y la evidencia
 *    sale al cumplirse cualquiera de los tres disparadores de FR-021.
 * 5. **Verificación** con reintento ante fallo de red hasta 2 veces **con la misma evidencia y la
 *    misma clave de idempotencia** (FR-035, FR-035a).
 * 5b. **Reintento automático del modo autónomo** (T087, FR-045): si `POST /marks/autonomous`
 *    responde `422 INSUFFICIENT_EVIDENCE`, puede ser el falso positivo del anti-replay de slots —
 *    dos trabajadores apuntando a la vez a la misma pantalla comparten ráfaga (implementation-notes,
 *    lote L6). Como el mismo código cubre otros motivos por opacidad (FR-023), Staff no distingue
 *    la causa; en su lugar reintenta **una sola vez por intento**, en silencio (sin vibrar ni tocar
 *    `failedAttempts`), esperando `AUTONOMOUS_RETRY_DELAY_MS` con evidencia nueva — nunca reenvía
 *    la rechazada. La cámara y el `<video>` no se tocan. Un segundo rechazo sí es un fallo normal.
 * 6. **Éxito**: vibración breve y pantalla de confirmación; la cámara se apaga en cuanto hay marca.
 * 7. **`429` con espera** (T094; QA-7 de la 005): `RATE_LIMITED`/`TERMINAL_BUSY` con
 *    `retry_after_seconds` deshabilitan el reintento con cuenta regresiva; al cumplirse, la máquina
 *    pasa a `READY` (sin error, botón "Marcar"). El `RATE_LIMITED` del intervalo autónomo
 *    (`details.reason = 'AUTONOMOUS_WORKER_INTERVAL'`) tiene su propio texto en vez del genérico.
 *
 * Todo lo inyectable (cámara, decodificador, bucle, API, ubicación, reloj, clave) tiene su
 * parámetro para poder probar el flujo sin WebAssembly, `getUserMedia` ni `geolocation`.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { decodeFerminQr, RateLimitReasonSchema } from '@fermin/contracts';
import type {
  AttendanceAction,
  ChallengeRef,
  LocationSample,
  StaffStreamMessage,
  WaitChallengeResponse,
} from '@fermin/contracts';

import { ApiError, NetworkError } from '../../api/client';
import { queryClient } from '../../api/query-client';
import { openStaffStream as openStaffStreamImpl } from '../../api/stream';
import {
  autonomousEvidenceTrigger,
  collectAutonomousFrame,
  createDecoder as createDecoderImpl,
  emptyAutonomousCollection,
  isAutonomousQr,
  openCamera as openCameraImpl,
  startScanner as startScannerImpl,
  submitAutonomousMark as submitAutonomousMarkImpl,
  toAutonomousFrames,
  CameraError,
  createScanStats,
  toCameraTelemetry,
  toCaptureTelemetry,
  type AutonomousCollection,
  type CameraFailure,
  type CameraTelemetry,
  type ScanRead,
  type ScanRoi,
  type ScanStats,
} from '../../camera';
import {
  captureLocation as captureLocationImpl,
  hasShownLocationNotice,
  setLocationExplained,
  setLocationNoticeShown,
  useLocationExplained,
  type CaptureLocationOptions,
  type GeoOutcome,
  type GeoSample,
} from '../../geo';
import { t } from '../../i18n';
import { invalidateAttendanceViews } from '../history/api';

import {
  createChallenge as createChallengeImpl,
  verifyChallenge as verifyChallengeImpl,
  waitChallenge as waitChallengeImpl,
} from './api';
import {
  collectFrame,
  emptyCollection,
  evidenceTrigger,
  toObservedFrames,
  type FrameCollection,
} from './frames';
import { autonomousLocationGraceMs, normalLocationGraceMs } from './location-grace';
import {
  challengeShapeOf,
  createInitialMarkingState,
  isRetryDisabled,
  isRetryingAutonomous,
  markingReducer,
  retryDisabledSeconds,
  ringProgress,
  shouldOfferRetry,
  shouldResumeAutonomousRetry,
  shouldShowHelp,
  shouldSuggestSupport,
  type MarkingError,
  type MarkingState,
} from './machine';

import type { RefObject } from 'react';

/** Cadencia del tick que gobierna los plazos de FR-005 y el tope de evidencia de FR-021. */
export const MARKING_TICK_MS = 500;

/** FR-035: la evidencia se reenvía hasta dos veces más ante fallo de red. */
export const NETWORK_RETRIES = 2;

/**
 * Cadencia de la señal de espera mientras el challenge está en cola (FR-014: "una por segundo"),
 * usada hasta que el servidor diga la suya. La manda él: el `202 QUEUED` trae `wait_interval_ms` y
 * el stream lo repite en `stream_open`. Nunca se decide en el cliente (FR-037).
 */
export const DEFAULT_WAIT_INTERVAL_MS = 1_000;

/**
 * QA-3 de la 005: margen de espera por la ubicación con la evidencia lista. Tope 1,5 s, recortado
 * por un presupuesto por modo para no acercarse a la ventana del servidor (ver `location-grace.ts`).
 */
export { LOCATION_GRACE_MS } from './location-grace';

/**
 * QA-7 de la 005: `details.reason` del `429 RATE_LIMITED` por la regla de una marca autónoma por
 * trabajador cada N s (FR-045). El literal lo valida `RateLimitReasonSchema` de `@fermin/contracts`.
 */
const AUTONOMOUS_INTERVAL_REASON = RateLimitReasonSchema.enum.AUTONOMOUS_WORKER_INTERVAL;

/** Vibración de éxito y de error (design-system §6.2). */
export const VIBRATE_SUCCESS = 30;
export const VIBRATE_ERROR = [20, 40, 20];

/** Códigos tras los que volver a apuntar la cámara tiene sentido (FR-034). */
const RECOVERABLE_CODES = new Set([
  'TERMINAL_UNAVAILABLE',
  'TERMINAL_BUSY',
  'CHALLENGE_EXPIRED',
  'INSUFFICIENT_EVIDENCE',
  'CHALLENGE_ALREADY_USED',
  'CHALLENGE_NOT_FOUND',
  'ACTION_CHANGED',
  'RATE_LIMITED',
  'NETWORK',
  'VALIDATION_ERROR',
  'INTERNAL_ERROR',
  // US8/T060: el mismo mensaje que `TERMINAL_UNAVAILABLE`, a propósito (wiring §3.2) — misma
  // recuperación: reintentar apuntando de nuevo.
  'AUTONOMOUS_DISABLED',
]);

/**
 * `details.retry_after_seconds` de `RATE_LIMITED` y `TERMINAL_BUSY` (T094, `lib/errors.ts` de la
 * API). Se revalida igual que `actionChangedTarget`: `ApiError.details` es
 * `Record<string, unknown>`, así que un valor con forma distinta se descarta en vez de propagarse
 * sin validar.
 */
function retryAfterSecondsOf(error: ApiError): number | undefined {
  const raw = error.details?.['retry_after_seconds'];
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : undefined;
}

/** `details.reason` de un `429 RATE_LIMITED`, revalidado contra el catálogo cerrado del contrato. */
function rateLimitReasonOf(error: ApiError): string | undefined {
  const parsed = RateLimitReasonSchema.safeParse(error.details?.['reason']);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Traduce lo que devolvió la API al mensaje **exacto** del catálogo (FR-034). Nunca se muestra el
 * texto crudo del servidor si hay clave para el código, y nunca el detalle interno del fallo de
 * evidencia (FR-023: `TOO_FEW_FRAMES`, `WINDOW_EXCEEDED`… no llegan al cliente y tampoco se
 * inventan aquí).
 */
export function markingErrorOf(error: unknown): MarkingError {
  if (error instanceof ApiError) {
    const retryAfterSeconds = retryAfterSecondsOf(error);
    // QA-7: el intervalo autónomo no es "Demasiadas solicitudes": el trabajador acaba de marcar.
    const key =
      error.code === 'RATE_LIMITED' && rateLimitReasonOf(error) === AUTONOMOUS_INTERVAL_REASON
        ? 'attendance.error.autonomousInterval'
        : `common.errors.${error.code}`;
    const message = t(key);
    return {
      code: error.code,
      message: message === key ? error.message : message,
      recoverable: RECOVERABLE_CODES.has(error.code),
      ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
    };
  }
  if (error instanceof NetworkError) {
    return { code: 'NETWORK', message: t('common.errors.NETWORK'), recoverable: true };
  }
  return {
    code: 'INTERNAL_ERROR',
    message: t('common.errors.INTERNAL_ERROR'),
    recoverable: true,
  };
}

/**
 * `details.proposed_action` de un `409 ACTION_CHANGED` (FR-007; `lib/errors.ts#actionChanged`).
 * Se revalida en el cliente porque `ApiError.details` es `Record<string, unknown>` (el sobre de
 * error es `.catchall(z.unknown())` en `@fermin/contracts`): un valor con forma distinta no se usa
 * y el flujo cae al error genérico en vez de propagar algo sin validar.
 */
function actionChangedTarget(error: ApiError): AttendanceAction | null {
  const raw = error.details?.['proposed_action'];
  return raw === 'CHECK_IN' || raw === 'CHECK_OUT' ? raw : null;
}

/** Mensaje del catálogo para un fallo de cámara (FR-036). */
export function cameraErrorOf(failure: string): MarkingError {
  const key =
    failure === 'DENIED' ? 'attendance.camera.deniedBody' : 'attendance.camera.unavailableBody';
  return { code: `CAMERA_${failure}`, message: t(key), recoverable: false };
}

/** `GeoSample` (interno, camelCase) → `LocationSample` del contrato (US6, T065). */
function toLocationSample(sample: GeoSample): LocationSample {
  return {
    latitude: sample.latitude,
    longitude: sample.longitude,
    accuracy_m: sample.accuracyM,
    latency_ms: sample.latencyMs,
  };
}

export interface UseMarkingOptions {
  /** Acción que la pantalla de inicio mostraba (FR-001/FR-007). */
  proposedAction: AttendanceAction | null;
  openCamera?: typeof openCameraImpl;
  createDecoder?: typeof createDecoderImpl;
  startScanner?: typeof startScannerImpl;
  createChallenge?: typeof createChallengeImpl;
  verifyChallenge?: typeof verifyChallengeImpl;
  /** `POST /challenges/{id}/wait` (US4, T048); por defecto el de `./api.ts`. */
  waitChallenge?: typeof waitChallengeImpl;
  /** Stream de eventos del realm staff (US4, T047/T048); por defecto el de `api/stream.ts`. */
  openStaffStream?: typeof openStaffStreamImpl;
  /** `POST /marks/autonomous` (US8, T061); por defecto el de `src/camera/autonomous-mark.ts`. */
  submitAutonomousMark?: typeof submitAutonomousMarkImpl;
  /** Captura de ubicación en paralelo (US6, T065); por defecto `geo/location.ts#captureLocation`. */
  captureLocation?: (options?: CaptureLocationOptions) => Promise<GeoOutcome>;
  /** Reloj monotónico; por defecto `performance.now()`. */
  now?: () => number;
  /** Clave de idempotencia por intento (FR-035a); por defecto `crypto.randomUUID()`. */
  newIdempotencyKey?: () => string;
  vibrate?: (pattern: number | number[]) => void;
}

export interface Marking {
  state: MarkingState;
  /** `<video>` donde se pinta la cámara. */
  videoRef: RefObject<HTMLVideoElement | null>;
  /** FR-005: ayuda a los 8 s ("Separa un poco el teléfono"). */
  help: boolean;
  /** FR-005: reintento ofrecido a los 20 s. */
  retryOffered: boolean;
  /** FR-035/US7 AC4: tras 3 challenges fallidos consecutivos en 2 min. */
  suggestSupport: boolean;
  /** T087/FR-045: `true` mientras se espera el reintento automático del modo autónomo. */
  autonomousRetry: boolean;
  /** T094: `true` mientras "Intentar nuevamente" debe quedar deshabilitado (`RATE_LIMITED` /
   * `TERMINAL_BUSY` con `retry_after_seconds`). */
  retryDisabled: boolean;
  /** T094: segundos restantes de esa espera; `0` fuera de ella. */
  retryDisabledSeconds: number;
  /** Avance 0–100 de "Verificando…" (barra bajo el marco, QA-4), o `undefined` sin barra. */
  progress: number | undefined;
  /** Telemetría de la cámara (`VPP_INIT`); nunca se muestra. */
  cameraTelemetry: CameraTelemetry | null;
  /**
   * T096: recorte vigente del bucle de captura. La pantalla dibuja el marco de enfoque **desde
   * aquí**; si se dibujara de otro sitio, la guía dejaría de corresponder a lo que se lee.
   */
  roi: ScanRoi | null;
  /** US6/T067: primera vez que se pide ubicación en esta sesión; explicación previa pendiente. */
  showLocationExplanation: boolean;
  /** El trabajador cerró la explicación de ubicación (US6, T067). No bloquea nada: la ubicación ya
   * se pidió en paralelo desde que se abrió la cámara. */
  acknowledgeLocationExplanation: () => void;
  /** US6/T068: aviso no modal, una vez por sesión, cuando no hubo ubicación disponible. */
  locationNotice: boolean;
  /** Volver a buscar marcador sin cerrar la cámara. */
  retry: () => void;
  /** Confirma la acción corregida tras un `ACTION_CHANGED` y vuelve a buscar (US2, T036). */
  confirmActionChanged: () => void;
}

function defaultNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function defaultIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Sin `randomUUID` (WebView antigua): UUIDv4 con `getRandomValues`.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function defaultVibrate(pattern: number | number[]): void {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(pattern);
  }
}

export function useMarking(options: UseMarkingOptions): Marking {
  const {
    proposedAction,
    openCamera = openCameraImpl,
    createDecoder = createDecoderImpl,
    startScanner = startScannerImpl,
    createChallenge = createChallengeImpl,
    verifyChallenge = verifyChallengeImpl,
    waitChallenge = waitChallengeImpl,
    openStaffStream = openStaffStreamImpl,
    submitAutonomousMark = submitAutonomousMarkImpl,
    captureLocation = captureLocationImpl,
    now = defaultNow,
    newIdempotencyKey = defaultIdempotencyKey,
    vibrate = defaultVibrate,
  } = options;

  /**
   * Las dependencias inyectadas viven en una ref y no en las listas de dependencias: si el
   * llamador las crea en línea (lo natural en una prueba), una lista con `openCamera` dentro
   * reabriría la cámara en **cada render**, y cada apertura provoca otro render. La ref las
   * mantiene frescas sin atar el ciclo de vida de la cámara al de los renders.
   */
  const depsRef = useRef({
    openCamera,
    createDecoder,
    startScanner,
    createChallenge,
    verifyChallenge,
    waitChallenge,
    openStaffStream,
    submitAutonomousMark,
    captureLocation,
    now,
    newIdempotencyKey,
    vibrate,
  });
  // Se declara el **primer** efecto del hook a propósito: los efectos corren en orden de
  // declaración, así que la ref ya está fresca cuando corre el de la cámara.
  useEffect(() => {
    depsRef.current = {
      openCamera,
      createDecoder,
      startScanner,
      createChallenge,
      verifyChallenge,
      waitChallenge,
      openStaffStream,
      submitAutonomousMark,
      captureLocation,
      now,
      newIdempotencyKey,
      vibrate,
    };
  });

  const [state, dispatch] = useReducer(markingReducer, undefined, () =>
    createInitialMarkingState({ proposedAction, now: now() }),
  );
  /**
   * El trabajador ya pidió abrir la cámara. Que **esté** abierta se deriva de esto y de la fase:
   * con marca creada o fallo no recuperable se apaga sola, sin un `setState` dentro de un efecto
   * que provocaría un render en cascada.
   */
  const [cameraRequested, setCameraRequested] = useState(true);
  const [cameraTelemetry, setCameraTelemetry] = useState<CameraTelemetry | null>(null);
  /** T096: recorte que está usando el bucle de captura; lo dibuja el marco de enfoque. */
  const [roi, setRoi] = useState<ScanRoi | null>(null);
  /** US6/T068: aviso no modal de ubicación no disponible, una sola vez por sesión. Se enciende
   * desde el `.then()` de `captureLocation` (permitido: no es un `setState` directo en el cuerpo
   * síncrono del efecto) y se apaga al abrir una cámara nueva de verdad (`retry`). */
  const [locationNotice, setLocationNotice] = useState(false);
  /** US6/T067: `true` si nunca se explicó el permiso de ubicación en esta sesión de navegador
   * (`geo/explanation-store.ts`, `sessionStorage`). Puramente derivado: no hace falta un
   * `setState` dentro del efecto que abre la cámara para encenderlo. */
  const locationExplained = useLocationExplained();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  /**
   * 006 · T028 (research R-16, R-18): al entrar en `SUCCESS` se invalidan el estado y el
   * historial —así el Inicio llega ya refrescado cuando `success-screen.tsx` regrese solo o al
   * toque—. El `queryClient` es el singleton de `api/query-client.ts`: se llama directamente sobre
   * él (no `useQueryClient()`) para no exigirle un `QueryClientProvider` a las pruebas de este
   * hook, que lo montan solo. Una ref evita invalidar en cada render mientras la fase sigue en
   * `SUCCESS` (p. ej. al recibir `mark` en el mismo render).
   */
  const invalidatedOnSuccessRef = useRef(false);
  useEffect(() => {
    if (state.phase === 'SUCCESS') {
      if (!invalidatedOnSuccessRef.current) {
        invalidatedOnSuccessRef.current = true;
        void invalidateAttendanceViews(queryClient);
      }
    } else {
      invalidatedOnSuccessRef.current = false;
    }
  }, [state.phase]);

  /** La colección viaja por ref porque se actualiza a ritmo de cámara, no de render. */
  const framesRef = useRef<FrameCollection>(emptyCollection());
  /** Colección del modo autónomo (US8, T061); vacía fuera de ese modo. */
  const autonomousFramesRef = useRef<AutonomousCollection>(emptyAutonomousCollection());
  /** Evita dos verificaciones del mismo challenge (o del mismo intento autónomo) en vuelo. */
  const verifyingRef = useRef(false);
  /**
   * T087/FR-045: `true` si este intento de cámara ya consumió su reintento automático tras un
   * `INSUFFICIENT_EVIDENCE` en modo autónomo. Se reinicia con cada cámara nueva de verdad
   * (`retry`/apertura) para que un reintento manual también tenga su propio margen.
   */
  const autonomousAutoRetryUsedRef = useRef(false);
  const idempotencyRef = useRef<string | null>(null);
  const cameraOpenedAtRef = useRef<number | null>(null);
  /**
   * T090 · FR-038/FR-039 ("errores de cámara"): motivo del fallo de cámara **pendiente de
   * reportar**. La clasificación ocurre en `src/camera/camera.ts` (por el `name` del error real de
   * `getUserMedia`, research R-25) pero el teléfono no tiene dónde dejarla: el único transporte es
   * `client.camera_failure` del siguiente `POST /challenges` que llegue a salir. Se consume una
   * sola vez — el intento que por fin abrió la cámara lo arrastra y lo suelta.
   */
  const cameraFailureReportRef = useRef<CameraFailure | null>(null);
  const searchStartedAtRef = useRef<number | null>(null);
  /**
   * Telemetría de rendimiento del bucle de captura (`scan-stats.ts`) del intento de cámara en
   * curso, y la de la propia cámara. Viajan en `POST /challenges` (ventana de búsqueda del
   * Bootstrap Code) y en `verify` (ventana de la ráfaga) para diagnosticar teléfonos lentos. Solo
   * números; ninguna decisión depende de ellas.
   */
  const scanStatsRef = useRef<ScanStats | null>(null);
  const cameraTelemetryRef = useRef<CameraTelemetry | null>(null);

  /** Telemetría de captura de la ventana que termina ahora, y abre la siguiente. */
  const takeCaptureTelemetry = useCallback(() => {
    const stats = scanStatsRef.current;
    const camera = cameraTelemetryRef.current;
    const nowMs = depsRef.current.now();
    const capture = stats === null ? undefined : toCaptureTelemetry(stats.snapshot(nowMs));
    stats?.reset(nowMs);
    return {
      ...(camera === null ? {} : { camera: toCameraTelemetry(camera) }),
      ...(capture === undefined ? {} : { capture }),
    };
  }, []);
  /**
   * Última muestra de ubicación resuelta para el intento de cámara en curso (US6, T065); `null`
   * mientras no llegó ninguna. Se reenvía en `verify` y en `POST /marks/autonomous` — el servidor
   * ignora `body.location` de `POST /challenges` (wiring, DRIFT), así que mandarla solo ahí la
   * perdería.
   */
  const locationRef = useRef<LocationSample | null>(null);
  /**
   * Se incrementa en cada apertura de cámara. Una promesa de `captureLocation` de un intento
   * anterior que resuelve tarde se descarta comparando contra el valor vigente (R-24: "si llega
   * después, se descarta").
   */
  const locationGenerationRef = useRef(0);
  /**
   * QA-3: promesa de la lectura de ubicación **todavía en curso** del intento de cámara vigente;
   * `null` cuando ya resolvió (con lectura o sin ella) o no hay cámara. La consulta
   * `waitForPendingLocation` antes de enviar la evidencia.
   */
  const locationPendingRef = useRef<Promise<void> | null>(null);
  /**
   * Cadencia de la señal de espera, tal como la dijo el servidor (`202 QUEUED` o `stream_open`).
   * Vive en una ref y no en la máquina porque no es estado que se pinte: solo gobierna un intervalo.
   */
  const waitIntervalRef = useRef(DEFAULT_WAIT_INTERVAL_MS);

  /**
   * QA-3: si la ubicación sigue en curso, la espera como máximo `graceMs` (la gracia efectiva ya
   * recortada por el presupuesto del modo, `location-grace.ts`). Nunca rechaza. Devuelve `false`
   * si, al terminar la espera, el intento ya no es el mismo (reintento, cámara nueva): entonces la
   * evidencia no se envía.
   */
  const waitForPendingLocation = useCallback(
    async (idempotencyKey: string, graceMs: number): Promise<boolean> => {
      const pending = locationPendingRef.current;
      if (pending !== null && graceMs > 0) {
        await new Promise<void>((resolve) => {
          const timer = globalThis.setTimeout(resolve, graceMs);
          void pending.then(() => {
            globalThis.clearTimeout(timer);
            resolve();
          });
        });
      }
      return idempotencyRef.current === idempotencyKey;
    },
    [],
  );

  /** Verificación con la **misma** evidencia y la misma clave en los reintentos (FR-035/FR-035a). */
  const submitEvidence = useCallback(
    async (challenge: ChallengeRef, collection: FrameCollection): Promise<void> => {
      const idempotencyKey = idempotencyRef.current;
      if (idempotencyKey === null) {
        return;
      }
      const { verifyChallenge: verify, vibrate: buzz } = depsRef.current;
      dispatch({ type: 'verifying' });
      // QA-3: la evidencia está lista; si la ubicación viene en camino, se le da un margen corto
      // que nunca pasa del presupuesto de la ventana de verificación del challenge.
      const graceMs = normalLocationGraceMs(challenge, collection, depsRef.current.now());
      if (!(await waitForPendingLocation(idempotencyKey, graceMs))) {
        return;
      }
      const body = {
        frames: toObservedFrames(collection),
        idempotency_key: idempotencyKey,
        // US6/T065: se reenvía aquí la muestra capturada en paralelo con la cámara — el servidor
        // ignora `body.location` de `POST /challenges` (wiring, DRIFT), así que `verify` es el
        // único sitio donde de verdad cuenta. `null` si no llegó ni con el margen de
        // `LOCATION_GRACE_MS` recortado por el presupuesto (nunca bloquea el VPP más que eso).
        location: locationRef.current,
        // Rendimiento de la captura en esta ráfaga (diagnóstico de teléfonos lentos). Se toma
        // una vez: los reintentos de red reenvían el **mismo** cuerpo (FR-035a).
        client: takeCaptureTelemetry(),
      };
      for (let attempt = 0; attempt <= NETWORK_RETRIES; attempt += 1) {
        try {
          const response = await verify(challenge.id, body);
          dispatch({ type: 'verified', mark: response.mark });
          buzz(VIBRATE_SUCCESS);
          return;
        } catch (error) {
          if (error instanceof NetworkError && attempt < NETWORK_RETRIES) {
            continue;
          }
          dispatch({ type: 'failed', error: markingErrorOf(error) });
          buzz(VIBRATE_ERROR);
          return;
        }
      }
    },
    [takeCaptureTelemetry, waitForPendingLocation],
  );

  const maybeVerify = useCallback(
    (challenge: ChallengeRef, collection: FrameCollection, atMs: number): void => {
      if (verifyingRef.current) {
        return;
      }
      const trigger = evidenceTrigger(collection, challengeShapeOf(challenge), atMs);
      if (trigger === null) {
        return;
      }
      verifyingRef.current = true;
      void submitEvidence(challenge, collection);
    },
    [submitEvidence],
  );

  /** Envío de la evidencia del **modo autónomo** (US8, T061); mismo reintento que `submitEvidence`. */
  const submitAutonomousEvidence = useCallback(
    async (collection: AutonomousCollection): Promise<void> => {
      const idempotencyKey = idempotencyRef.current;
      const tsid = collection.tsid;
      if (idempotencyKey === null || tsid === null) {
        return;
      }
      const { submitAutonomousMark: submit, vibrate: buzz } = depsRef.current;
      dispatch({ type: 'verifying' });
      // QA-3: mismo margen, con el presupuesto del modo autónomo (desde el primer frame).
      const graceMs = autonomousLocationGraceMs(collection.firstAtMs, depsRef.current.now());
      if (!(await waitForPendingLocation(idempotencyKey, graceMs))) {
        return;
      }
      const body = {
        tsid,
        frames: toAutonomousFrames(collection),
        idempotency_key: idempotencyKey,
        proposed_action: stateRef.current.proposedAction,
        // US6/T065: misma muestra que el modo normal; `AutonomousMarkBodySchema.location` sí la
        // usa (a diferencia de `POST /challenges`, aquí no hay DRIFT que la ignore).
        location: locationRef.current,
      };
      for (let attempt = 0; attempt <= NETWORK_RETRIES; attempt += 1) {
        try {
          const response = await submit(body);
          dispatch({ type: 'verified', mark: response.mark });
          buzz(VIBRATE_SUCCESS);
          return;
        } catch (error) {
          if (error instanceof NetworkError && attempt < NETWORK_RETRIES) {
            continue;
          }
          // T087/FR-045: el primer `INSUFFICIENT_EVIDENCE` de este intento en modo autónomo se
          // trata como el posible falso positivo del anti-replay de slots — no como un fallo. Se
          // descarta la evidencia rechazada (nunca se reenvía) y se espera con la cámara viva;
          // `verifyingRef` sigue en `true` durante la espera (lo libera el efecto de tick al
          // cumplirse el plazo) para que no se dispare una verificación temprana.
          if (
            error instanceof ApiError &&
            error.code === 'INSUFFICIENT_EVIDENCE' &&
            !autonomousAutoRetryUsedRef.current
          ) {
            autonomousAutoRetryUsedRef.current = true;
            autonomousFramesRef.current = emptyAutonomousCollection();
            idempotencyRef.current = null;
            dispatch({ type: 'autonomous_retry_scheduled', now: depsRef.current.now() });
            return;
          }
          dispatch({ type: 'failed', error: markingErrorOf(error) });
          buzz(VIBRATE_ERROR);
          return;
        }
      }
    },
    [waitForPendingLocation],
  );

  const maybeVerifyAutonomous = useCallback(
    (collection: AutonomousCollection, atMs: number): void => {
      if (verifyingRef.current) {
        return;
      }
      const trigger = autonomousEvidenceTrigger(collection, atMs);
      if (trigger === null) {
        return;
      }
      verifyingRef.current = true;
      void submitAutonomousEvidence(collection);
    },
    [submitAutonomousEvidence],
  );

  /** Bootstrap leído → `POST /challenges` (FR-004). Del QR no sale nada más. */
  const requestChallenge = useCallback(
    async (tsid: string, bt: string): Promise<void> => {
      const { createChallenge: create, now: clock, newIdempotencyKey: newKey, vibrate: buzz } =
        depsRef.current;
      const openedAt = cameraOpenedAtRef.current;
      const searchStartedAt = searchStartedAtRef.current;
      // T090: se reporta una sola vez; si esta petición falla, el fallo de cámara ya viajó igual
      // (la API lo emite antes de tocar la transacción).
      const cameraFailure = cameraFailureReportRef.current;
      cameraFailureReportRef.current = null;
      const nowMs = clock();
      // Ventana de búsqueda del Bootstrap Code: se cierra aquí y abre la de la ráfaga.
      const captureTelemetry = takeCaptureTelemetry();
      idempotencyRef.current = newKey();
      framesRef.current = emptyCollection();
      verifyingRef.current = false;
      try {
        const result = await create({
          tsid,
          bt,
          proposed_action: stateRef.current.proposedAction,
          // FR-004: "la ubicación si ya está disponible". El servidor la ignora aquí hoy (wiring,
          // DRIFT) — se manda de todos modos porque no cuesta nada y es lo que pide el requisito;
          // lo que de verdad cuenta es el reenvío en `verify` (arriba).
          location: locationRef.current,
          client: {
            ...(openedAt === null ? {} : { camera_open_ms: Math.max(0, Math.round(openedAt)) }),
            ...(searchStartedAt === null
              ? {}
              : { bootstrap_search_ms: Math.max(0, Math.round(nowMs - searchStartedAt)) }),
            // FR-038: `CAMERA_DENIED`/`CAMERA_ERROR` del intento anterior. Solo el motivo del
            // catálogo cerrado; el error del navegador nunca sale del teléfono.
            ...(cameraFailure === null ? {} : { camera_failure: cameraFailure }),
            // Resolución/fps reales y rendimiento del bucle mientras buscaba (solo números).
            ...captureTelemetry,
          },
        });
        if (result.status === 'PENDING') {
          dispatch({
            type: 'challenge_created',
            challenge: result.challenge,
            idempotencyKey: idempotencyRef.current,
          });
          return;
        }
        waitIntervalRef.current = result.wait_interval_ms;
        dispatch({
          type: 'challenge_queued',
          challenge: result.challenge,
          position: result.position,
          etaMs: result.eta_ms,
        });
      } catch (error) {
        // FR-007/US2: discrepancia — el servidor no llegó a crear ningún challenge. Se muestra la
        // acción correcta y se pide confirmación explícita (T036); no es un fallo, así que no
        // vibra como uno.
        if (error instanceof ApiError && error.code === 'ACTION_CHANGED') {
          const action = actionChangedTarget(error);
          if (action !== null) {
            dispatch({ type: 'action_changed', action });
            return;
          }
        }
        dispatch({ type: 'failed', error: markingErrorOf(error) });
        buzz(VIBRATE_ERROR);
      }
    },
    [takeCaptureTelemetry],
  );

  /**
   * Estado del challenge tal como lo cuenta el servidor, venga del latido (`POST /{id}/wait`) o del
   * stream (US4 · T048). Un solo sitio decide qué hacer con él, para que la degradación no cambie el
   * comportamiento de la pantalla: es literalmente el mismo código.
   *
   * `VERIFYING`/`SUCCESS` se dejan pasar sin tocar: la confirmación autoritativa es siempre el `200`
   * de `verify` (FR-014a) y un aviso redundante no puede pisarla.
   */
  const applyServerState = useCallback(
    (challengeId: string, update: Pick<WaitChallengeResponse, 'status' | 'position' | 'eta_ms' | 'mark'>): void => {
      const current = stateRef.current;
      if (current.challenge?.id !== challengeId) {
        return;
      }
      if (current.phase === 'VERIFYING' || current.phase === 'SUCCESS') {
        return;
      }
      if (update.status === 'QUEUED') {
        if (update.position !== null && update.eta_ms !== null) {
          dispatch({ type: 'queue_position', position: update.position, etaMs: update.eta_ms });
        }
        return;
      }
      if (update.status === 'USED' && update.mark !== null) {
        // El turno llegó, se reprodujo y la marca existe, pero la respuesta de `verify` se perdió
        // (red intermitente): se recupera la confirmación sin volver a verificar.
        dispatch({ type: 'verified', mark: update.mark });
        depsRef.current.vibrate(VIBRATE_SUCCESS);
        return;
      }
      // `PENDING` o `DISPLAYING`: el turno ya es suyo y la cámara debe dejar de esperar.
      if (current.phase === 'QUEUED') {
        // La espera en cola no es la ráfaga: la ventana de telemetría empieza con el turno.
        scanStatsRef.current?.reset(depsRef.current.now());
      }
      dispatch({ type: 'challenge_displaying' });
    },
    [],
  );

  /**
   * Una señal de espera (FR-014, FR-014a). Es a la vez el latido que mantiene vivo el turno en la
   * cola y la **degradación** del stream: su respuesta trae el mismo estado.
   */
  const sendWaitSignal = useCallback(
    async (challengeId: string): Promise<void> => {
      const { waitChallenge: wait, vibrate: buzz } = depsRef.current;
      try {
        const response = await wait(challengeId);
        applyServerState(challengeId, response);
      } catch (error) {
        // Solo el **veredicto del servidor** termina la marcación. Un latido perdido (red, o
        // cualquier fallo del cliente) no cancela nada: si se pierden todos, el servidor liberará el
        // turno y el siguiente latido que sí llegue traerá el fallo definitivo (410/422).
        if (!(error instanceof ApiError) || stateRef.current.challenge?.id !== challengeId) {
          return;
        }
        dispatch({ type: 'failed', error: markingErrorOf(error) });
        buzz(VIBRATE_ERROR);
      }
    },
    [applyServerState],
  );

  /** Un mensaje del stream de eventos del realm staff (T047). Nunca decide nada por sí solo. */
  const handleStreamMessage = useCallback(
    (message: StaffStreamMessage): void => {
      if (message.type === 'stream_open') {
        waitIntervalRef.current = message.wait_interval_ms;
        return;
      }
      if (message.type === 'queue_position') {
        applyServerState(message.challenge_id, {
          status: 'QUEUED',
          position: message.position,
          eta_ms: message.eta_ms,
          mark: null,
        });
        return;
      }
      if (message.type === 'challenge_displaying') {
        applyServerState(message.challenge_id, {
          status: 'DISPLAYING',
          position: null,
          eta_ms: null,
          mark: null,
        });
        return;
      }
      if (message.status === 'USED') {
        applyServerState(message.challenge_id, {
          status: 'USED',
          position: null,
          eta_ms: null,
          mark: message.mark,
        });
        return;
      }
      // `FAILED` / `EXPIRED`: el mismo código opaco que habría devuelto la ruta (FR-023). Solo
      // aplica si el challenge sigue siendo el de la pantalla y la evidencia no salió ya.
      const current = stateRef.current;
      if (
        current.challenge?.id !== message.challenge_id ||
        current.phase === 'VERIFYING' ||
        current.phase === 'SUCCESS'
      ) {
        return;
      }
      const code = message.error_code ?? 'CHALLENGE_EXPIRED';
      const key = `common.errors.${code}`;
      const text = t(key);
      dispatch({
        type: 'failed',
        error: { code, message: text === key ? t('common.errors.INTERNAL_ERROR') : text, recoverable: true },
      });
      depsRef.current.vibrate(VIBRATE_ERROR);
    },
    [applyServerState],
  );

  const handleStreamMessageRef = useRef(handleStreamMessage);
  useEffect(() => {
    handleStreamMessageRef.current = handleStreamMessage;
  }, [handleStreamMessage]);

  /**
   * Incorpora una lectura `FM1A` (modo autónomo, US8/T061): la genera la clave de idempotencia al
   * primer frame de un intento nuevo (nunca al reintentar: FR-035a exige repetirla) y dispara la
   * verificación cuando corresponda. Devuelve `true` si el texto era un frame autónomo, se haya
   * aceptado o no (fuera de rango, repetido, de otro marcador…) — el llamador no debe caer al
   * camino del challenge normal en ese caso.
   */
  const handleAutonomousRead = useCallback(
    (read: ScanRead): boolean => {
      if (!isAutonomousQr(read.text)) {
        return false;
      }
      if (stateRef.current.autonomousRetryUntil !== null) {
        // T087: en espera del reintento automático (FR-045) — se descarta hasta reanudar.
        return true;
      }
      const wasEmpty = autonomousFramesRef.current.tsid === null;
      const next = collectAutonomousFrame(autonomousFramesRef.current, read);
      if (next === autonomousFramesRef.current) {
        return true;
      }
      autonomousFramesRef.current = next;
      if (wasEmpty) {
        idempotencyRef.current = depsRef.current.newIdempotencyKey();
        verifyingRef.current = false;
      }
      dispatch({ type: 'autonomous_frames', frames: next });
      maybeVerifyAutonomous(next, read.capturedAtMs);
      return true;
    },
    [maybeVerifyAutonomous],
  );

  /** Cada lectura de la cámara, en orden de captura. */
  const handleRead = useCallback(
    (read: ScanRead): void => {
      const current = stateRef.current;
      if (current.phase === 'SEARCHING') {
        if (handleAutonomousRead(read)) {
          return;
        }
        const decoded = decodeFerminQr(read.text);
        if (decoded === null || decoded.kind !== 'bootstrap') {
          return;
        }
        dispatch({ type: 'bootstrap_found' });
        void requestChallenge(decoded.tsid, decoded.bt);
        return;
      }
      if (current.phase === 'CHALLENGE' && current.autonomous) {
        handleAutonomousRead(read);
        return;
      }
      if (current.phase !== 'CHALLENGE' && current.phase !== 'QUEUED') {
        return;
      }
      const challenge = current.challenge;
      if (challenge === null) {
        return;
      }
      const next = collectFrame(framesRef.current, read, challengeShapeOf(challenge));
      if (next === framesRef.current) {
        return;
      }
      framesRef.current = next;
      dispatch({ type: 'frames', frames: next });
      maybeVerify(challenge, next, read.capturedAtMs);
    },
    [handleAutonomousRead, maybeVerify, requestChallenge],
  );

  const handleReadRef = useRef(handleRead);
  useEffect(() => {
    handleReadRef.current = handleRead;
  }, [handleRead]);

  /**
   * Con marca creada, cámara bloqueada o fallo no recuperable la cámara se apaga: no se captura
   * más de lo imprescindible (FR-031). Un error **recuperable** la deja abierta para que
   * "Intentar nuevamente" no pierda el enfoque.
   */
  const cameraOn =
    cameraRequested &&
    state.phase !== 'SUCCESS' &&
    state.phase !== 'CAMERA_BLOCKED' &&
    !(state.phase === 'ERROR' && state.error?.recoverable === false);

  /** US6/T067: se pide en cuanto la cámara está en marcha, salvo que ya se haya explicado antes en
   * esta sesión de navegador. No bloquea nada: la ubicación ya se pidió en paralelo (R-24). */
  const showLocationExplanation = cameraOn && !locationExplained;

  /** Cámara + decodificador + bucle: se abren una vez y viven todo el intento. */
  useEffect(() => {
    if (!cameraOn) {
      return;
    }
    let active = true;
    let stopScanner: (() => void) | null = null;
    let camera: Awaited<ReturnType<typeof openCameraImpl>> | null = null;
    let decoder: ReturnType<typeof createDecoderImpl> | null = null;

    const {
      openCamera: open,
      createDecoder: makeDecoder,
      startScanner: scan,
      captureLocation: captureLoc,
      now: clock,
    } = depsRef.current;

    // T087: cámara nueva de verdad ⇒ nuevo margen de un reintento automático del modo autónomo.
    autonomousAutoRetryUsedRef.current = false;

    // US6/T065 · research R-24: la ubicación se pide "en el mismo tick en que se abre la cámara",
    // en paralelo y sin bloquear nada de lo de abajo. Un intento nuevo de cámara descarta la
    // muestra del anterior (generación); `showLocationExplanation` (T067) se deriva de
    // `useLocationExplained()` más abajo, no hace falta encenderla aquí.
    locationRef.current = null;
    const locationGeneration = (locationGenerationRef.current += 1);
    const locationPending: Promise<void> = captureLoc().then((outcome) => {
      // QA-3: resolvió (con lectura o sin ella): ya no hay nada que esperar al enviar.
      if (locationPendingRef.current === locationPending) {
        locationPendingRef.current = null;
      }
      if (!active || locationGenerationRef.current !== locationGeneration) {
        // Cámara cerrada o intento nuevo ya en curso: la respuesta tardía se descarta (R-24).
        return;
      }
      if (outcome.status === 'ok') {
        locationRef.current = toLocationSample(outcome.sample);
        return;
      }
      locationRef.current = null;
      if (!hasShownLocationNotice()) {
        setLocationNoticeShown(true);
        setLocationNotice(true);
      }
    });
    locationPendingRef.current = locationPending;

    const start = async (): Promise<void> => {
      const startedAt = clock();
      try {
        camera = await open();
      } catch (error) {
        if (!active) {
          return;
        }
        const failure = error instanceof CameraError ? error.failure : 'ERROR';
        // T090: queda pendiente de reportar hasta el próximo `POST /challenges` (FR-038).
        cameraFailureReportRef.current = failure;
        dispatch({ type: 'camera_failed', failure, error: cameraErrorOf(failure) });
        return;
      }
      if (!active) {
        camera.stop();
        return;
      }
      cameraOpenedAtRef.current = clock() - startedAt;
      cameraTelemetryRef.current = camera.telemetry;
      setCameraTelemetry(camera.telemetry);

      const video = videoRef.current;
      if (video !== null) {
        video.srcObject = camera.stream;
        video.muted = true;
        video.playsInline = true;
        try {
          await video.play();
        } catch {
          // Reproducción bloqueada por política de autoplay: el bucle aguanta hasta que arranque.
        }
      }
      if (!active) {
        camera.stop();
        return;
      }
      const openedAt = clock();
      searchStartedAtRef.current = openedAt;
      dispatch({ type: 'camera_opened', now: openedAt });

      decoder = makeDecoder();
      const stats = createScanStats(openedAt);
      scanStatsRef.current = stats;
      if (video !== null) {
        stopScanner = scan({
          video,
          decoder,
          stats,
          onRead: (read) => handleReadRef.current(read),
          // T096: cada cambio de recorte llega a la interfaz para que el marco lo siga.
          onRoi: (next) => {
            if (active) {
              setRoi(next);
            }
          },
          now: clock,
        });
      }
    };

    void start();

    const attachedVideo = videoRef.current;
    return () => {
      active = false;
      locationPendingRef.current = null;
      stopScanner?.();
      decoder?.close();
      camera?.stop();
      scanStatsRef.current = null;
      cameraTelemetryRef.current = null;
      // T096: el recorte del intento anterior no vale para el siguiente (otro encuadre, otra
      // distancia); se limpia aquí, en la limpieza del efecto, no en su cuerpo.
      setRoi(null);
      if (attachedVideo !== null) {
        attachedVideo.srcObject = null;
      }
    };
  }, [cameraOn]);

  /** Tick: plazos de FR-005 y tope de 2.5 s de la evidencia (FR-021) si dejaran de llegar frames. */
  useEffect(() => {
    const timer = globalThis.setInterval(() => {
      const current = stateRef.current;
      const nowMs = depsRef.current.now();
      dispatch({ type: 'tick', now: nowMs });
      if (current.phase === 'CHALLENGE' && current.challenge !== null) {
        maybeVerify(current.challenge, framesRef.current, nowMs);
      }
      // T087/FR-045: se cumplió el plazo del reintento automático del modo autónomo — se libera
      // `verifyingRef` (lo dejó en `true` el rechazo) y se reanuda la captura de evidencia nueva.
      if (shouldResumeAutonomousRetry(current, nowMs)) {
        verifyingRef.current = false;
        dispatch({ type: 'autonomous_retry_resumed' });
      }
    }, MARKING_TICK_MS);
    return () => {
      globalThis.clearInterval(timer);
    };
  }, [maybeVerify]);

  /**
   * US4 · T048: mientras el challenge espera turno, una señal de espera por intervalo. Es el único
   * latido que mantiene vivo el turno (sin él, el servidor lo expira a los
   * `queue_wait_heartbeat_timeout_ms` y libera el terminal) y a la vez la vía de degradación del
   * stream. **La cámara no se toca**: sigue abierta y leyendo, así que en cuanto el marcador empiece
   * a pintar, los frames entran solos (`handleRead` acepta lecturas en `QUEUED`).
   */
  const queuedChallengeId = state.phase === 'QUEUED' ? (state.challenge?.id ?? null) : null;
  useEffect(() => {
    if (queuedChallengeId === null) {
      return;
    }
    // La primera señal sale de inmediato: confirma al servidor que la cámara ya está apuntando.
    void sendWaitSignal(queuedChallengeId);
    const timer = globalThis.setInterval(() => {
      void sendWaitSignal(queuedChallengeId);
    }, waitIntervalRef.current);
    return () => {
      globalThis.clearInterval(timer);
    };
  }, [queuedChallengeId, sendWaitSignal]);

  /**
   * US4 · T047/T048: el stream de eventos vive tanto como la cámara. Se abre una vez por intento y
   * se cierra al salir; la reconexión con backoff la lleva el propio cliente
   * (`api/stream.ts`), porque el servidor corta por inactividad y eso es normal.
   *
   * Si no abre o se corta, **no pasa nada**: la señal de espera trae el mismo estado (FR-014a).
   */
  useEffect(() => {
    if (!cameraOn) {
      return;
    }
    const subscription = depsRef.current.openStaffStream({
      onMessage: (message) => handleStreamMessageRef.current(message),
    });
    return () => {
      subscription.close();
    };
  }, [cameraOn]);

  const retry = useCallback(() => {
    framesRef.current = emptyCollection();
    autonomousFramesRef.current = emptyAutonomousCollection();
    verifyingRef.current = false;
    idempotencyRef.current = null;
    // T087: un reintento manual también se gana su propio margen de un reintento automático.
    autonomousAutoRetryUsedRef.current = false;
    const nowMs = depsRef.current.now();
    searchStartedAtRef.current = nowMs;
    scanStatsRef.current?.reset(nowMs);
    if (!cameraOn) {
      // T071: "Intentar nuevamente" tras un fallo de cámara. `cameraOn` pasa a `true` y reabre el
      // efecto de arriba sin desmontar el `<video>` (sigue siendo el mismo nodo, ver camera-stage):
      // por eso el reintento no pierde el enfoque ni deja la cámara "muerta en silencio". Cámara
      // nueva de verdad ⇒ se vuelve a pedir la ubicación, así que el aviso viejo ya no aplica.
      setLocationNotice(false);
      dispatch({ type: 'request_camera' });
      setCameraRequested(true);
      return;
    }
    dispatch({ type: 'retry', now: nowMs });
  }, [cameraOn]);

  /**
   * US2/T036: el trabajador confirmó la acción corregida tras un `ACTION_CHANGED`. La cámara
   * nunca se cerró (§ arriba), así que solo hace falta volver a buscar el marcador; no se
   * reproduce ningún challenge porque no llegó a crearse ninguno.
   */
  const confirmActionChanged = useCallback(() => {
    framesRef.current = emptyCollection();
    autonomousFramesRef.current = emptyAutonomousCollection();
    verifyingRef.current = false;
    idempotencyRef.current = null;
    autonomousAutoRetryUsedRef.current = false;
    const nowMs = depsRef.current.now();
    searchStartedAtRef.current = nowMs;
    scanStatsRef.current?.reset(nowMs);
    dispatch({ type: 'confirm_action_changed', now: nowMs });
  }, []);

  /** US6/T067: el trabajador cerró la explicación de ubicación. No reintenta nada: la petición de
   * ubicación ya salió en paralelo con la cámara (R-24). */
  const acknowledgeLocationExplanation = useCallback(() => {
    // `showLocationExplanation` se deriva de `useLocationExplained()`: marcar el flag ya alcanza
    // para que la hoja se cierre en el siguiente render, sin un `setState` local duplicado.
    setLocationExplained(true);
  }, []);

  const progress = useMemo(() => ringProgress(state), [state]);

  return {
    state,
    videoRef,
    help: shouldShowHelp(state),
    retryOffered: shouldOfferRetry(state),
    suggestSupport: shouldSuggestSupport(state),
    autonomousRetry: isRetryingAutonomous(state),
    retryDisabled: isRetryDisabled(state),
    retryDisabledSeconds: retryDisabledSeconds(state),
    progress,
    cameraTelemetry,
    roi,
    showLocationExplanation,
    acknowledgeLocationExplanation,
    locationNotice,
    retry,
    confirmActionChanged,
  };
}
